import { httpClient } from "./client";
import type {
  ProgramApi,
  ProgramListParams,
  ProgramSessionWindow,
  ProgramSessionPreviewWindow,
  ProgramTrackOptionsParams,
  PublicProgramTrackOptionsParams,
  ProgramEnrollBody,
  ProgramEnrollmentQuoteBody,
  ClassLearningPageParams,
} from "./interfaces";
import type {
  UnifiedProgramResponse,
  ProgramTrackResponse,
  UnifiedProgramSessionResponse,
  UnifiedEnrollmentResponse,
  EnrollmentModeQuoteResponse,
  ProgramLearningSpaceResponse,
  BoundedPublicProgramDetailResponse,
  PublicProgramTrackOptionsPage,
  PublicProgramSessionPreviewPage,
  ProgramTrackOptionsPage,
  ProgramTrackOfferingLabelResponse,
  ProgramSessionPreviewPage,
  PromotionClaimRequest,
  Schema,
} from "../types/generated";

/**
 * programApi — the authenticated player/staff surface of the unified program module
 * (router /programs, ADR-006/006a). All endpoints sit behind get_current_user;
 * facility-scoped reads fall back to the public facility so a player can browse
 * any publicly-visible facility's PUBLISHED programs (discovery is facility-first
 * — there is no cross-facility "clinics near me" feed).
 *
 * Thin client: fee, available_spots, eligibility, and the enrolled-vs-waitlisted
 * outcome are ALL server-computed. The frontend forwards selections and opaque
 * quote references, a stripe_payment_method_id (paid + seated) and optional
 * student_age (age-gated tracks); it never derives capacity, price or fees.
 */
export const programApi: ProgramApi = {
  listPublicPrograms: (params) => {
    const sp = new URLSearchParams({ facility_id: params.facility_id });
    if (params.program_type) sp.set("program_type", params.program_type);
    if (params.limit !== undefined) sp.set("limit", String(params.limit));
    if (params.offset !== undefined) sp.set("offset", String(params.offset));
    return httpClient.get(`/programs/public?${sp.toString()}`);
  },

  getPublicProgram: (programId: string, facilityId: string) =>
    httpClient.get<BoundedPublicProgramDetailResponse>(
      `/programs/${programId}/public?facility_id=${encodeURIComponent(facilityId)}`,
    ),

  getPublicTrackOptions: (
    programId,
    facilityId,
    params: PublicProgramTrackOptionsParams = {},
    signal,
  ) => {
    const sp = new URLSearchParams({ facility_id: facilityId });
    if (params.limit !== undefined) sp.set("limit", String(params.limit));
    if (params.cursor) sp.set("cursor", params.cursor);
    return httpClient.get<PublicProgramTrackOptionsPage>(
      `/programs/${encodeURIComponent(programId)}/public/track-options?${sp}`,
      { signal },
    );
  },

  getPublicSessionPreview: (programId, facilityId, window, signal) => {
    const sp = previewParams(window);
    sp.set("facility_id", facilityId);
    return httpClient.get<PublicProgramSessionPreviewPage>(
      `/programs/${encodeURIComponent(programId)}/public/session-preview?${sp}`,
      { signal },
    );
  },

  listPrograms: (params: ProgramListParams, signal) => {
    const sp = new URLSearchParams();
    sp.set("facility_id", params.facility_id);
    if (params.status) sp.set("status", params.status);
    if (params.program_type) sp.set("program_type", params.program_type);
    if (params.limit !== undefined) sp.set("limit", String(params.limit));
    if (params.offset !== undefined) sp.set("offset", String(params.offset));
    return httpClient.get<{ items: UnifiedProgramResponse[]; total: number }>(
      `/programs?${sp.toString()}`,
      { signal },
    );
  },

  getProgram: (programId: string) =>
    httpClient.get<UnifiedProgramResponse>(`/programs/${programId}`),

  getTracks: (
    programId: string,
    params: ProgramTrackOptionsParams = {},
    signal,
  ) => {
    const sp = new URLSearchParams();
    if (params.active_only !== undefined)
      sp.set("active_only", String(params.active_only));
    if (params.limit !== undefined) sp.set("limit", String(params.limit));
    if (params.cursor) sp.set("cursor", params.cursor);
    return httpClient.get<ProgramTrackOptionsPage>(
      `/programs/${encodeURIComponent(programId)}/tracks${sp.size ? `?${sp}` : ""}`,
      { signal },
    );
  },

  getTrack: (programId, trackId, signal) =>
    httpClient.get<ProgramTrackResponse>(
      `/programs/${encodeURIComponent(programId)}/tracks/${encodeURIComponent(trackId)}`,
      { signal },
    ),

  getTrackOfferingLabel: (trackId, facilityId, signal) =>
    httpClient.get<ProgramTrackOfferingLabelResponse>(
      `/programs/tracks/${encodeURIComponent(trackId)}/offering-label?facility_id=${encodeURIComponent(facilityId)}`,
      { signal },
    ),

  getSessionPreview: (programId, window, signal) =>
    httpClient.get<ProgramSessionPreviewPage>(
      `/programs/${encodeURIComponent(programId)}/session-preview?${previewParams(window)}`,
      { signal },
    ),

  // The backend validates window_end > window_start and 422s otherwise, so the
  // caller MUST always pass a bounded window.
  getSessions: (programId: string, window: ProgramSessionWindow) => {
    const sp = new URLSearchParams();
    sp.set("window_start", window.window_start);
    sp.set("window_end", window.window_end);
    if (window.track_id) sp.set("track_id", window.track_id);
    return httpClient.get<UnifiedProgramSessionResponse[]>(
      `/programs/${programId}/sessions?${sp.toString()}`,
    );
  },

  // The router owns identity and track binding; the generated body carries
  // selections, opaque quote references and optional age/card inputs.
  enroll: (trackId: string, body: ProgramEnrollBody) =>
    httpClient.post<UnifiedEnrollmentResponse>(
      `/programs/tracks/${trackId}/enroll`,
      body,
    ),

  quoteEnrollment: (trackId: string, body: ProgramEnrollmentQuoteBody) =>
    httpClient.post<EnrollmentModeQuoteResponse>(
      `/programs/tracks/${trackId}/enrollment-quote`,
      body,
    ),

  getPromotionPaymentReview: (enrollmentId: string) =>
    httpClient.get(
      `/programs/enrollments/${encodeURIComponent(enrollmentId)}/payment-review`,
    ),

  getLearningSpace: (programId: string) =>
    httpClient.get<ProgramLearningSpaceResponse>(
      `/programs/${programId}/learning-space`,
    ),

  listLearningLearners: (
    programId: string,
    params: ClassLearningPageParams = {},
    signal?: AbortSignal,
  ) => {
    const sp = new URLSearchParams();
    if (params.limit !== undefined) sp.set("limit", String(params.limit));
    if (params.offset !== undefined) sp.set("offset", String(params.offset));
    const query = sp.toString();
    return httpClient.get<Schema["ClassLearningLearners"]>(
      `/programs/${encodeURIComponent(programId)}/learning-learners${query ? `?${query}` : ""}`,
      { signal },
    );
  },

  getLearningProgress: (
    programId: string,
    enrollmentId: string,
    params: ClassLearningPageParams = {},
    signal?: AbortSignal,
  ) => {
    const sp = new URLSearchParams();
    if (params.limit !== undefined) sp.set("limit", String(params.limit));
    if (params.offset !== undefined) sp.set("offset", String(params.offset));
    const query = sp.toString();
    return httpClient.get<Schema["ClassLearningProgress"]>(
      `/programs/${encodeURIComponent(programId)}/learning-progress/${encodeURIComponent(enrollmentId)}${query ? `?${query}` : ""}`,
      { signal },
    );
  },

  saveSessionFeedback: (
    programId: string,
    body: Schema["ClassSessionFeedbackWrite"],
  ) =>
    httpClient.put<Schema["ClassSessionFeedback"]>(
      `/programs/${encodeURIComponent(programId)}/session-notes`,
      body,
    ),

  getAttendanceRoster: (
    trackId: string,
    occurrenceStart: string,
    params: ClassLearningPageParams = {},
    signal?: AbortSignal,
  ) => {
    const sp = new URLSearchParams({ occurrence_start: occurrenceStart });
    if (params.limit !== undefined) sp.set("limit", String(params.limit));
    if (params.offset !== undefined) sp.set("offset", String(params.offset));
    return httpClient.get<Schema["ClassAttendanceRoster"]>(
      `/programs/tracks/${encodeURIComponent(trackId)}/attendance-roster?${sp}`,
      { signal },
    );
  },

  getClassRetention: (programId, enrollmentId, signal) => {
    const query = enrollmentId
      ? `?enrollment_id=${encodeURIComponent(enrollmentId)}`
      : "";
    return httpClient.get(
      `/programs/${encodeURIComponent(programId)}/academy/retention${query}`,
      { signal },
    );
  },

  configureClassRetention: (programId, body) =>
    httpClient.put(
      `/programs/${encodeURIComponent(programId)}/academy/retention`,
      body,
    ),

  listClassMaterials: (programId, params = {}, signal) => {
    const query = new URLSearchParams();
    if (params.enrollment_id) query.set("enrollment_id", params.enrollment_id);
    if (params.limit !== undefined) query.set("limit", String(params.limit));
    if (params.offset !== undefined) query.set("offset", String(params.offset));
    const suffix = query.toString();
    return httpClient.get(
      `/programs/${encodeURIComponent(programId)}/academy/materials${suffix ? `?${suffix}` : ""}`,
      { signal },
    );
  },

  createClassMaterial: (programId, body) =>
    httpClient.post(
      `/programs/${encodeURIComponent(programId)}/academy/materials`,
      body,
    ),

  updateClassMaterial: (programId, materialId, body) =>
    httpClient.put(
      `/programs/${encodeURIComponent(programId)}/academy/materials/${encodeURIComponent(materialId)}`,
      body,
    ),

  transitionClassMaterial: (programId, materialId, transition, body) =>
    httpClient.post(
      `/programs/${encodeURIComponent(programId)}/academy/materials/${encodeURIComponent(materialId)}/${transition}`,
      body,
    ),

  listClassAssignments: (programId, params = {}, signal) => {
    const query = new URLSearchParams();
    if (params.enrollment_id) query.set("enrollment_id", params.enrollment_id);
    if (params.limit !== undefined) query.set("limit", String(params.limit));
    if (params.offset !== undefined) query.set("offset", String(params.offset));
    const suffix = query.toString();
    return httpClient.get(
      `/programs/${encodeURIComponent(programId)}/academy/assignments${suffix ? `?${suffix}` : ""}`,
      { signal },
    );
  },

  createClassAssignment: (programId, body) =>
    httpClient.post(
      `/programs/${encodeURIComponent(programId)}/academy/assignments`,
      body,
    ),

  updateClassAssignment: (programId, assignmentId, body) =>
    httpClient.put(
      `/programs/${encodeURIComponent(programId)}/academy/assignments/${encodeURIComponent(assignmentId)}`,
      body,
    ),

  publishClassAssignment: (programId, assignmentId, body) =>
    httpClient.post(
      `/programs/${encodeURIComponent(programId)}/academy/assignments/${encodeURIComponent(assignmentId)}/publish`,
      body,
    ),

  closeClassAssignment: (programId, assignmentId, body) =>
    httpClient.post(
      `/programs/${encodeURIComponent(programId)}/academy/assignments/${encodeURIComponent(assignmentId)}/close`,
      body,
    ),

  listClassAssignmentTargets: (
    programId,
    assignmentId,
    params = {},
    signal,
  ) => {
    const query = new URLSearchParams();
    if (params.limit !== undefined) query.set("limit", String(params.limit));
    if (params.offset !== undefined) query.set("offset", String(params.offset));
    const suffix = query.toString();
    return httpClient.get(
      `/programs/${encodeURIComponent(programId)}/academy/assignments/${encodeURIComponent(assignmentId)}/targets${suffix ? `?${suffix}` : ""}`,
      { signal },
    );
  },

  listClassSubmissions: (targetId, enrollmentId, params = {}, signal) => {
    const query = new URLSearchParams({ enrollment_id: enrollmentId });
    if (params.limit !== undefined) query.set("limit", String(params.limit));
    if (params.offset !== undefined) query.set("offset", String(params.offset));
    return httpClient.get(
      `/programs/academy/targets/${encodeURIComponent(targetId)}/submissions?${query}`,
      { signal },
    );
  },

  saveClassSubmission: (targetId, enrollmentId, body) =>
    httpClient.put(
      `/programs/academy/targets/${encodeURIComponent(targetId)}/submissions?enrollment_id=${encodeURIComponent(enrollmentId)}`,
      body,
    ),

  submitClassSubmission: (targetId, enrollmentId, submissionId, body) =>
    httpClient.post(
      `/programs/academy/targets/${encodeURIComponent(targetId)}/submissions/${encodeURIComponent(submissionId)}/submit?enrollment_id=${encodeURIComponent(enrollmentId)}`,
      body,
    ),

  reviewClassSubmission: (programId, submissionId, body) =>
    httpClient.put(
      `/programs/${encodeURIComponent(programId)}/academy/submissions/${encodeURIComponent(submissionId)}/review`,
      body,
    ),

  reserveClassFile: (programId, body) =>
    httpClient.post(
      `/programs/${encodeURIComponent(programId)}/academy/files/reserve`,
      body,
    ),

  uploadClassFile: (fileId, file) => {
    const form = new FormData();
    form.set("file", file);
    form.set("content_length", String(file.size));
    return httpClient.putForm(
      `/programs/academy/files/${encodeURIComponent(fileId)}/content`,
      form,
    );
  },

  downloadClassFile: (fileId, enrollmentId) => {
    const query = enrollmentId
      ? `?enrollment_id=${encodeURIComponent(enrollmentId)}`
      : "";
    return httpClient.getBlob(
      `/programs/academy/files/${encodeURIComponent(fileId)}/content${query}`,
    );
  },

  removeClassFile: (fileId, body) =>
    httpClient.deleteWithBody(
      `/programs/academy/files/${encodeURIComponent(fileId)}`,
      body,
    ),

  getMyEnrollments: () =>
    httpClient.get<UnifiedEnrollmentResponse[]>("/programs/enrollments/me"),

  cancelEnrollment: (enrollmentId: string) =>
    httpClient.post<UnifiedEnrollmentResponse>(
      `/programs/enrollments/${enrollmentId}/cancel`,
    ),

  // Waitlist promotion: claim pays if the track has a fee (the body carries the
  // chosen card); decline frees the promoted spot.
  claimPromotion: (enrollmentId: string, body: PromotionClaimRequest = {}) =>
    httpClient.post<UnifiedEnrollmentResponse>(
      `/programs/enrollments/${enrollmentId}/claim`,
      body,
    ),
  resumeEnrollmentPayment: (enrollmentId: string) =>
    httpClient.post<UnifiedEnrollmentResponse>(
      `/programs/enrollments/${encodeURIComponent(enrollmentId)}/resume-payment`,
    ),

  declinePromotion: (enrollmentId: string) =>
    httpClient.post<UnifiedEnrollmentResponse>(
      `/programs/enrollments/${enrollmentId}/decline`,
    ),
};

function previewParams(window: ProgramSessionPreviewWindow): URLSearchParams {
  const sp = new URLSearchParams({
    window_start: window.window_start,
    window_end: window.window_end,
  });
  if (window.track_id) sp.set("track_id", window.track_id);
  if (window.limit !== undefined) sp.set("limit", String(window.limit));
  if (window.cursor) sp.set("cursor", window.cursor);
  return sp;
}
